import React from 'react';
import { RouteComponentProps } from 'react-router';
import { useIonAlert, useIonRouter, useIonToast } from '@ionic/react';
import { Button } from 'react-bootstrap';
import { axius, cartFx, func, images, types } from 'utils';
import { _store, _update } from '_Store';
import Touchable from './Touchable';

interface StateProps {
	item: types.Item;
	layout: 'list' | 'grid';
	promo?: any;
	store?: any;
	canAdd2Cart?: boolean;
	description?: boolean;

	handleSelectedItem?: (item: types.Item) => void;
	handleCloseModal?: () => void;
}

interface Props extends RouteComponentProps, types.StateProps, StateProps {}

const Item: React.FC<Props> = (props) => {
	const [presentAlert] = useIonAlert();
	const [presentToast] = useIonToast();
	const router = useIonRouter();

	const [addingToFavorite, setAddingToFavorite] = React.useState(false);

	const { item, layout, promo } = props;
	const {
		__data: { cart, promo_cart, activeStore, promotions },
		__auth: { isLoggedIn, favorites, user },
	} = _store.useState();

	React.useEffect(() => {
		// console.log(images[Math.floor(Math.random() * 5)]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const store = activeStore || props.store || item.store || {};
	const isOpen = func.isOpen(store);
	// const itemLink = `/i/${func.seourl(item.name)}.${activeStore.code}.${item.code}`;
	const itemImage =
		(item.images[0]
			? item.imagesLinks[0]
			: store?.logo
				? activeStore.logoLink
				: images.Logo) || '';

	const add2Cart = () => {
		let inCart = ((cart.items || []).find((c) => c.id === item.id) ||
			{}) as types.Item;
		let toAdd = {
			id: item.id,
			uuid: item.id,
			code: item.code,
			name: item.name,
			price: +(inCart.price || item.price),
			images: item.images,
			imagesLinks: item.imagesLinks,
			priceTotal: +(inCart.priceTotal || item.price),
			description: item.description,
			variants: inCart.variants || {},
			preparationTime: item.preparationTime,
			categories: item.categories,
		};
		if (cart.store) {
			if (
				!cart.store.id ||
				store.id === cart.store.id ||
				cart.items.length === 0
			) {
				if (item.variants.length > 0) {
					goToItem();
				} else {
					let nCart = JSON.parse(JSON.stringify(cart)) as types.Cart;
					nCart['items'] = cartFx.add(
						toAdd as unknown as types.ItemCart,
						nCart.items
					);
					nCart['store'] = store;
					_update.data('cart', nCart);
					presentToast({
						message: `${item.name} added to cart!`,
						duration: 1500,
						position: 'top',
					});
				}
			} else {
				presentAlert({
					header: 'Sorry!',
					message: `You already have item(s) at "${func.venueName(cart.store)}". You can not add this item from "${func.venueName(store)}".`,
					buttons: [
						{ text: 'Okay' },
						{
							text: 'Empty cart',
							handler: () => {
								const nCart = {
									items: [] as types.ItemCart[],
									store: {},
								} as types.Cart;
								nCart['items'] = cartFx.add(
									toAdd as unknown as types.ItemCart,
									nCart.items
								);
								nCart['store'] = store;
								_update.data('cart', nCart);
								presentToast({
									message: `${item.name} added to cart!`,
									duration: 1500,
									position: 'top',
								});
							},
						},
					],
				});
			}
		} else {
			let nCart = { items: [] as types.ItemCart[], store: {} } as types.Cart;
			nCart['items'] = cartFx.add(
				toAdd as unknown as types.ItemCart,
				nCart.items
			);
			nCart['store'] = store;
			_update.data('cart', nCart);
			presentToast({
				message: `${item.name} added to cart!`,
				duration: 1500,
				position: 'top',
			});
		}
	};

	const add2Favorite = () => {
		setAddingToFavorite(true);
		let favItems = func.copyObject(favorites) as types.Item[];
		favItems = favItems.concat(item);
		_update.auth('favorites', favItems);
		axius.post(`users/me/favorites`, { itemCode: item.code }).then((res) => {
			if (res.status === 201) {
				presentToast({
					message: `Item added to your favorites`,
					duration: 1500,
					position: 'top',
				});
			}
			setAddingToFavorite(false);
		});
	};

	const removeFav = () => {
		setAddingToFavorite(true);
		let favItems = func.copyObject(favorites) as types.Item[];
		favItems = favItems.filter((fav) => fav.id !== item.id);
		_update.auth('favorites', favItems);
		axius.delte(`users/me/favorites/${item.code}`).then((res) => {
			if (res.status === 200) {
				presentToast({
					message: `Item removed from your favorites`,
					duration: 1500,
					position: 'top',
				});
			}
			setAddingToFavorite(false);
		});
	};

	const goToItem = async () => {
		props.handleSelectedItem?.(item);
	};

	const inCart = (promo || {}).id
		? (promo_cart.items || []).find((c: types.Item) => c.id === item.id) || {}
		: (cart.items || []).find((c: types.ItemCart) => c.id === item.id) ||
			({} as types.Item);
	const inFav =
		favorites.find((fav) => fav.id === item.id) || ({} as types.Item);
	const canAdd2Cart =
		typeof props.canAdd2Cart !== 'undefined' ? props.canAdd2Cart : true;
	const itemAvailableInStore =
		!activeStore.preferences?.itemsAvailable ||
		activeStore.preferences?.itemsAvailable.includes(item.code);
	const hasPricedVariants = item.variants.filter(
		(iv) => iv.options.filter((ivo) => ivo.price > 0).length > 0
	);
	const hasPromo = cartFx.myPromos({ promotions, item, user })[0];
	const discount = cartFx.promoDiscount(hasPromo, item.price);
	const promoValue = hasPromo
		? `${hasPromo.type === types.PromotionTypes.FLAT_OFF_ITEM ? `-₦${func.numberFormat(hasPromo.value)}` : ''}${hasPromo.type === types.PromotionTypes.PERCENTAGE_OFF_ITEM ? `-${func.numberFormat(hasPromo.value)}%` : ''}`
		: '';

	return (
		<React.Fragment>
			{item.status === types.ItemStatuses.PUBLISHED && itemAvailableInStore && (
				<div
					id="Item"
					className={`animate__animated animate__fadeIn pointer ${layout} ${item.id}`}
				>
					{layout === 'grid' && (
						<div id="grid">
							<Touchable onClick={goToItem}>
								<div
									className="bg-img contain"
									style={{
										borderBottom: '1px solid #eee',
										backgroundImage: `url("${itemImage}")`,
									}}
								>
									{hasPromo && (
										<div className="item-discount grid">
											<i className="fa fa-gift primary fs-12" />
										</div>
									)}
									{promoValue && (
										<div className="price small fw-500">{promoValue}</div>
									)}
									{!isOpen.open && (
										<div className="closed d-flex align-items-center justify-content-center fw-600">
											{isOpen.text}
										</div>
									)}
								</div>
								<div className="px-2s pt-1">
									<div className="name mb-1s">
										<div className="row">
											<div className="col-12 fw-500s">
												<div className="small text-truncate text-muted-">
													{item.name}
												</div>
												<div className="price small fw-500">
													₦
													{func.numberFormat(
														(item.priceDiscount || item.price) - discount
													)}
													{hasPricedVariants.length > 0 && '+'}
													{discount > 0 && (
														<span className="text-muted">
															&nbsp;
															<del>
																₦
																{func.numberFormat(
																	item.priceDiscount || item.price
																)}
															</del>
														</span>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</Touchable>
						</div>
					)}

					{layout === 'list' && (
						<div id="list">
							<div className="d-flex justify-content-betweens align-items-center">
								<Touchable onClick={goToItem}>
									<div
										className="bg-img contain"
										style={{
											backgroundImage: `url("${itemImage}")`,
											width: 80,
											height: 80,
											borderRadius: 5,
											// border: 0,
										}}
									>
										{!isOpen.open && (
											<div className="closed d-flex align-items-center justify-content-center fw-600">
												{isOpen.text}
											</div>
										)}
										{hasPromo && (
											<div className="item-discount m-1">
												<i className="fa fa-gift primary" />
											</div>
										)}
									</div>
								</Touchable>
								<div className="pl-3" style={{ width: 'calc(100% - 80px)' }}>
									<Touchable onClick={goToItem}>
										<div className="name fw-500 mb-1s text-truncate">
											{item.name}
										</div>
									</Touchable>
									{item.description && (
										<div
											className="small text-muted"
											onClick={goToItem}
											style={{ maxHeight: 38, overflow: 'hidden' }}
										>
											{item.description.substring(0, 90)}...
										</div>
									)}
									<Touchable onClick={goToItem}>
										<div className="primary fw-500 mt-2">
											{hasPricedVariants.length > 0 && 'From '}₦
											{func.numberFormat(
												(item.priceDiscount || item.price) - discount
											)}
											{discount > 0 && (
												<span className="text-muted small">
													&nbsp;
													<del>
														₦
														{func.numberFormat(
															item.priceDiscount || item.price
														)}
													</del>
												</span>
											)}
										</div>
									</Touchable>
									{/* <Touchable onClick={goToItem}>
										<div className="small text-muted mb-1 d-flex align-items-center">
											{inCart.id && (
												<div className="fs-12 mr-2s text-muted fw-500">
													&nbsp;•&nbsp;In cart
												</div>
											)}
										</div>
									</Touchable> */}
									<div className="d-flex- hides justify-content-betweens mt-2">
										{canAdd2Cart && !inCart.id && (
											<Button
												variant="primary-light btn-sm mr-2"
												onClick={add2Cart}
												className="hide"
											>
												<small className="fw-500">+ Add</small>
											</Button>
										)}
										{inCart.id && (
											<Button
												variant="primary btn-sm mr-2"
												onClick={() => router.push('/cart')}
											>
												&nbsp;<small className="fw-500">Cart</small>&nbsp;
											</Button>
										)}
										&nbsp;
										{isLoggedIn && (
											<>
												{inFav.id ? (
													<Button
														variant="clear btn-sm"
														disabled={addingToFavorite}
														onClick={removeFav}
													>
														<img
															src={images.BookmarkFilled}
															alt="Remove Bookmark"
															height={24}
														/>
													</Button>
												) : (
													<Button
														variant="clear btn-sm"
														disabled={addingToFavorite}
														onClick={add2Favorite}
													>
														<img
															src={images.Bookmark}
															alt="Bookmark"
															height={24}
														/>
													</Button>
												)}
											</>
										)}
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</React.Fragment>
	);
};

export default Item;
